import { ArrowRight, Check } from "lucide-react";
import { Button } from "../ui/button";
import SectionLogo from "../SectionLogo";

const BASEBALL_AREAS = [
  "Throwing Shoulder & Rotator Cuff",
  "Elbow & UCL Area",
  "Forearm & Wrist",
  "Knees & Hips (Catchers)",
  "Lower Back & Core",
  "Hamstrings & Quads",
];

const WHY_CHOOSE = [
  { point: "Dugout Ready", desc: "We set up right at the field — treat players between innings or after the game" },
  { point: "Arm Care", desc: "Targeted treatment for pitchers' shoulders and elbows to reduce inflammation fast" },
  { point: "Full Team Coverage", desc: "Treat the entire roster — pitchers, catchers, infielders, outfielders" },
  { point: "Prevent Injuries", desc: "Regular cryo sessions help prevent overuse injuries that sideline players" },
  {
    point: "Tournament Ready",
    desc: "Perfect for tournament weekends when players compete in multiple games back-to-back",
  },
];

export const BaseballSection = ({ scrollToSection }) => {
  return (
    <section id="baseball" className="py-24 bg-[#0a0318] relative">
      <SectionLogo position="left" size="medium" opacity={0.05} />
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block bg-amber-500/10 border border-amber-500/30 rounded-full px-6 py-2 mb-6">
              <span className="text-amber-300 font-medium">Baseball Recovery</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-amber-400">Baseball</span> <span className="text-white">Player Recovery</span>
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              From pitchers&apos; arms to catchers&apos; knees, Ice Man Cryo delivers targeted cryotherapy to keep
              baseball players on the field and performing at their best.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-6">
              <div className="bg-[#110422]/50 border border-amber-500/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-amber-400 mb-4">For Pitchers</h3>
                <p className="text-white/70 mb-4">
                  Pitching puts enormous stress on the shoulder, elbow, and forearm. After throwing 80-100+ pitches,
                  inflammation sets in fast. The SubZero device targets the rotator cuff, UCL area, and forearm muscles
                  to bring down inflammation immediately. Pitchers who use cryotherapy between starts recover faster and
                  maintain arm health throughout the season.
                </p>
                <h3 className="text-2xl font-bold text-amber-400 mb-4 mt-6">For Position Players</h3>
                <p className="text-white/70 mb-4">
                  Catchers deal with constant knee and hip strain from squatting. Outfielders and infielders take hits
                  to their shoulders from throwing and diving. Batters deal with wrist, forearm, and back tightness from
                  swinging. Cryotherapy treats all of these areas on the spot — right in the dugout or locker room.
                </p>
              </div>

              <h3 className="text-xl font-bold text-white">Treatment Areas for Baseball</h3>
              <div className="space-y-3">
                {BASEBALL_AREAS.map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-3 bg-[#110422]/50 border border-amber-500/20 rounded-lg p-4"
                  >
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-600 to-amber-400 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-white">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-[#110422]/50 border border-amber-500/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-4">Why Baseball Teams Choose Ice Man Cryo</h3>
                <div className="space-y-3">
                  {WHY_CHOOSE.map((item) => (
                    <div key={item.point} className="border-b border-amber-500/10 pb-3 last:border-0">
                      <span className="text-amber-400 font-semibold">{item.point}:</span>
                      <span className="text-white/70 ml-2">{item.desc}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#110422]/50 border border-amber-500/20 rounded-2xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6">Pricing</h3>
                <div className="space-y-2">
                  <h4 className="text-amber-400 font-bold text-sm uppercase tracking-wider">Team Packages</h4>
                  <div className="flex justify-between items-center py-3 border-b border-amber-500/20">
                    <span className="text-gray-300">Single Game / Event</span>
                    <span className="text-amber-400 font-bold text-lg">$400–$600</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-amber-500/20">
                    <span className="text-gray-300">3-Game Package</span>
                    <span className="text-amber-400 font-bold text-lg">$1,050</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-amber-500/20">
                    <span className="text-gray-300">Season Package</span>
                    <span className="text-amber-400 font-bold text-lg">Contact Us</span>
                  </div>
                </div>
                <div className="space-y-2 mt-6">
                  <h4 className="text-amber-400 font-bold text-sm uppercase tracking-wider">Individual Packages</h4>
                  <div className="flex justify-between items-center py-3 border-b border-amber-500/20">
                    <span className="text-gray-300">Single Session</span>
                    <span className="text-amber-400 font-bold text-lg">$40–$80</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-amber-500/20">
                    <span className="text-gray-300">3-Session Package</span>
                    <span className="text-amber-400 font-bold text-lg">$75</span>
                  </div>
                  <div className="flex justify-between items-center py-3 border-b border-amber-500/20">
                    <span className="text-gray-300">Monthly Unlimited</span>
                    <span className="text-amber-400 font-bold text-lg">Contact Us</span>
                  </div>
                </div>
                <div className="py-3 mt-4">
                  <p className="text-white/70 text-sm mb-2">Price depends on the number of players and time.</p>
                  <p className="text-amber-400 font-semibold text-sm">
                    All packages must be prepaid. 20% discount on bulk bookings.
                  </p>
                </div>
                <div className="mt-6">
                  <Button
                    onClick={() => scrollToSection("booking")}
                    className="w-full bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-700 hover:to-amber-600 text-white py-6"
                    data-testid="baseball-book-btn"
                  >
                    Book Baseball Recovery <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
